import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';

interface Survey {
  id: string;
  title: string;
  description?: string;
  status: 'draft' | 'published' | 'closed';
  startDate?: string;
  endDate?: string;
  targetCount?: number;
  responseCount: number;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  isAnonymous: boolean;
  hasResponded: boolean;
}

interface Question {
  id: string;
  surveyId: string;
  type: 'single' | 'multiple' | 'text' | 'scale' | 'select';
  text: string;
  options?: string[];
  isRequired: boolean;
  order: number;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
}

interface QuestionResponse {
  questionId: string;
  answer: string | string[] | number;
}

export default function SurveyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState<Record<string, string | string[] | number>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [deleteQuestionTarget, setDeleteQuestionTarget] = useState<string | null>(null);

  const isAuthor = survey?.createdBy === user?.id;

  useEffect(() => {
    fetchSurvey();
    fetchQuestions();
  }, [id]);

  const fetchSurvey = async () => {
    setLoading(true);
    try {
      const data = await api.get<Survey>(`/api/surveys/${id}`);
      setSurvey(data);
    } catch {
      navigate('/surveys');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const data = await api.get<{ items: Question[] }>(`/api/surveys/${id}/questions`);
      setQuestions(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setQuestions([]);
    }
  };

  const handleResponseChange = (questionId: string, value: string | string[] | number) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleMultipleChange = (questionId: string, option: string, checked: boolean) => {
    const current = (responses[questionId] as string[]) || [];
    if (checked) {
      setResponses((prev) => ({ ...prev, [questionId]: [...current, option] }));
    } else {
      setResponses((prev) => ({ ...prev, [questionId]: current.filter((o) => o !== option) }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    
    // 必須項目チェック
    for (const q of questions) {
      if (q.isRequired && !responses[q.id]) {
        toast.error(`「${q.text}」は必須項目です`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const submissionData: QuestionResponse[] = Object.entries(responses).map(([questionId, answer]) => ({
        questionId,
        answer,
      }));
      await api.post(`/api/surveys/${id}/responses`, { responses: submissionData });
      toast.success('回答を送信しました');
      navigate('/surveys');
    } catch {
      toast.error('回答の送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    try {
      await api.put(`/api/surveys/${id}`, { status: 'published' });
      fetchSurvey();
    } catch {
      toast.error('公開に失敗しました');
    }
  };

  const handleClose = async () => {
    try {
      await api.put(`/api/surveys/${id}`, { status: 'closed' });
      fetchSurvey();
    } catch {
      toast.error('終了に失敗しました');
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setShowQuestionModal(true);
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setShowQuestionModal(true);
  };

  const handleDeleteQuestion = (questionId: string) => {
    setDeleteQuestionTarget(questionId);
  };

  const confirmDeleteQuestion = async () => {
    if (!deleteQuestionTarget) return;
    try {
      await api.delete(`/api/surveys/${id}/questions/${deleteQuestionTarget}`);
      fetchQuestions();
    } catch {
      toast.error('削除に失敗しました');
    } finally {
      setDeleteQuestionTarget(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'warning' | 'success' | 'danger'> = {
      draft: 'default',
      published: 'success',
      closed: 'danger',
    };
    const labels: Record<string, string> = {
      draft: '下書き',
      published: '公開中',
      closed: '終了',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">アンケートが見つかりません</p>
        <Link to="/surveys" className="text-blue-600 hover:underline mt-4 inline-block">
          一覧に戻る
        </Link>
      </div>
    );
  }

  const canRespond = survey.status === 'published' && !survey.hasResponded;
  const canEdit = isAuthor && survey.status === 'draft';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/surveys')}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          一覧に戻る
        </Button>
        <div className="flex gap-2">
          {isAuthor && (
            <Link to={`/surveys/${id}/results`}>
              <Button variant="secondary">結果を見る</Button>
            </Link>
          )}
          {canEdit && (
            <>
              <Button variant="secondary" onClick={() => setShowEditModal(true)}>
                編集
              </Button>
              <Button onClick={handlePublish}>公開する</Button>
            </>
          )}
          {isAuthor && survey.status === 'published' && (
            <Button variant="danger" onClick={handleClose}>終了する</Button>
          )}
        </div>
      </div>

      {/* アンケート情報 */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {getStatusBadge(survey.status)}
            {survey.isAnonymous && <Badge variant="info">匿名</Badge>}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{survey.title}</h1>
          {survey.description && (
            <p className="text-gray-600 dark:text-gray-400">{survey.description}</p>
          )}
          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
            <span>作成者: {survey.createdByName}</span>
            {survey.endDate && (
              <span>回答期限: {new Date(survey.endDate).toLocaleDateString('ja-JP')}</span>
            )}
            <span>回答数: {survey.responseCount}{survey.targetCount ? `/${survey.targetCount}` : ''}</span>
          </div>
          {survey.hasResponded && (
            <div className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-3 rounded-lg text-sm">
              ✅ 回答済みです
            </div>
          )}
        </div>
      </Card>

      {/* 質問編集モード（作成者のみ） */}
      {canEdit && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">質問一覧</h2>
            <Button size="sm" onClick={handleAddQuestion}>
              質問を追加
            </Button>
          </div>
          {questions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">質問がありません。質問を追加してください。</p>
          ) : (
            <div className="space-y-4">
              {questions.sort((a, b) => a.order - b.order).map((q, idx) => (
                <div key={q.id} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="text-lg font-bold text-gray-400">{idx + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="default">
                        {q.type === 'single' ? '単一選択' : q.type === 'multiple' ? '複数選択' : q.type === 'text' ? 'テキスト' : q.type === 'scale' ? '評価' : 'セレクト'}
                      </Badge>
                      {q.isRequired && <Badge variant="danger">必須</Badge>}
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white">{q.text}</p>
                    {q.options && (
                      <ul className="mt-2 text-sm text-gray-500">
                        {q.options.map((opt, i) => (
                          <li key={i}>・{opt}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEditQuestion(q)} className="text-blue-600 hover:text-blue-800 text-sm">
                      編集
                    </button>
                    <button onClick={() => handleDeleteQuestion(q.id)} className="text-red-600 hover:text-red-800 text-sm">
                      削除
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* 回答フォーム */}
      {canRespond && (
        <form onSubmit={handleSubmit}>
          {questions.sort((a, b) => a.order - b.order).map((q, idx) => (
            <Card key={q.id} className="mb-4">
              <div className="space-y-4">
                <div className="flex items-start gap-2">
                  <span className="text-lg font-bold text-blue-600">{idx + 1}.</span>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {q.text}
                      {q.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </p>
                  </div>
                </div>

                {/* 単一選択 */}
                {q.type === 'single' && q.options && (
                  <div className="space-y-2 pl-6">
                    {q.options.map((option, i) => (
                      <label key={i} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name={q.id}
                          value={option}
                          checked={responses[q.id] === option}
                          onChange={() => handleResponseChange(q.id, option)}
                          className="w-4 h-4"
                        />
                        <span className="text-gray-700 dark:text-gray-300">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* 複数選択 */}
                {q.type === 'multiple' && q.options && (
                  <div className="space-y-2 pl-6">
                    {q.options.map((option, i) => (
                      <label key={i} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={((responses[q.id] as string[]) || []).includes(option)}
                          onChange={(e) => handleMultipleChange(q.id, option, e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-gray-700 dark:text-gray-300">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* テキスト */}
                {q.type === 'text' && (
                  <textarea
                    value={(responses[q.id] as string) || ''}
                    onChange={(e) => handleResponseChange(q.id, e.target.value)}
                    placeholder="回答を入力してください"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                )}

                {/* 評価スケール */}
                {q.type === 'scale' && (
                  <div className="pl-6">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">{q.scaleMinLabel || q.scaleMin || 1}</span>
                      <div className="flex gap-2">
                        {Array.from({ length: (q.scaleMax || 5) - (q.scaleMin || 1) + 1 }, (_, i) => {
                          const value = (q.scaleMin || 1) + i;
                          return (
                            <button
                              key={value}
                              type="button"
                              onClick={() => handleResponseChange(q.id, value)}
                              className={`w-10 h-10 rounded-full border-2 font-medium transition-colors ${
                                responses[q.id] === value
                                  ? 'border-blue-500 bg-blue-500 text-white'
                                  : 'border-gray-300 text-gray-600 hover:border-blue-300'
                              }`}
                            >
                              {value}
                            </button>
                          );
                        })}
                      </div>
                      <span className="text-sm text-gray-500">{q.scaleMaxLabel || q.scaleMax || 5}</span>
                    </div>
                  </div>
                )}

                {/* セレクトボックス */}
                {q.type === 'select' && q.options && (
                  <div className="pl-6">
                    <Select
                      value={(responses[q.id] as string) || ''}
                      onChange={(e) => handleResponseChange(q.id, e.target.value)}
                    >
                      <option value="">選択してください</option>
                      {q.options.map((option, i) => (
                        <option key={i} value={option}>{option}</option>
                      ))}
                    </Select>
                  </div>
                )}
              </div>
            </Card>
          ))}

          <div className="flex justify-end">
            <Button type="submit" disabled={submitting}>
              {submitting ? '送信中...' : '回答を送信'}
            </Button>
          </div>
        </form>
      )}

      {/* 回答済みまたは終了の場合の質問表示 */}
      {(survey.hasResponded || survey.status === 'closed') && !canEdit && (
        <div className="space-y-4">
          {questions.sort((a, b) => a.order - b.order).map((q, idx) => (
            <Card key={q.id}>
              <div className="flex items-start gap-2">
                <span className="text-lg font-bold text-gray-400">{idx + 1}.</span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{q.text}</p>
                  {q.options && (
                    <ul className="mt-2 text-sm text-gray-500">
                      {q.options.map((opt, i) => (
                        <li key={i}>・{opt}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* 質問削除確認モーダル */}
      <Modal isOpen={!!deleteQuestionTarget} onClose={() => setDeleteQuestionTarget(null)} title="質問の削除">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">この質問を削除しますか？</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteQuestionTarget(null)}>キャンセル</Button>
            <Button variant="danger" onClick={confirmDeleteQuestion}>削除</Button>
          </div>
        </div>
      </Modal>

      {/* 質問追加/編集モーダル */}
      <Modal
        isOpen={showQuestionModal}
        onClose={() => setShowQuestionModal(false)}
        title={editingQuestion ? '質問を編集' : '質問を追加'}
        size="lg"
      >
        <QuestionForm
          surveyId={id!}
          question={editingQuestion}
          questionCount={questions.length}
          onSave={() => {
            setShowQuestionModal(false);
            fetchQuestions();
          }}
          onCancel={() => setShowQuestionModal(false)}
        />
      </Modal>

      {/* アンケート編集モーダル */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="アンケートを編集">
        <SurveyEditForm
          survey={survey}
          onSave={() => {
            setShowEditModal(false);
            fetchSurvey();
          }}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>
    </div>
  );
}

// 質問フォーム
function QuestionForm({
  surveyId,
  question,
  questionCount,
  onSave,
  onCancel,
}: {
  surveyId: string;
  question: Question | null;
  questionCount: number;
  onSave: () => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    type: question?.type || 'single' as 'single' | 'multiple' | 'text' | 'scale' | 'select',
    text: question?.text || '',
    options: question?.options?.join('\n') || '',
    isRequired: question?.isRequired ?? true,
    order: question?.order ?? questionCount + 1,
    scaleMin: question?.scaleMin ?? 1,
    scaleMax: question?.scaleMax ?? 5,
    scaleMinLabel: question?.scaleMinLabel || '',
    scaleMaxLabel: question?.scaleMaxLabel || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        options: formData.options ? formData.options.split('\n').filter((o) => o.trim()) : undefined,
      };
      if (question) {
        await api.put(`/api/surveys/${surveyId}/questions/${question.id}`, submitData);
      } else {
        await api.post(`/api/surveys/${surveyId}/questions`, submitData);
      }
      onSave();
    } catch {
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const showOptions = ['single', 'multiple', 'select'].includes(formData.type);
  const showScale = formData.type === 'scale';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">質問タイプ</label>
        <Select
          value={formData.type}
          onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as typeof formData.type }))}
        >
          <option value="single">単一選択</option>
          <option value="multiple">複数選択</option>
          <option value="text">テキスト入力</option>
          <option value="scale">評価スケール</option>
          <option value="select">セレクトボックス</option>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          質問文 <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.text}
          onChange={(e) => setFormData((prev) => ({ ...prev, text: e.target.value }))}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
          required
        />
      </div>

      {showOptions && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            選択肢（1行に1つ） <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.options}
            onChange={(e) => setFormData((prev) => ({ ...prev, options: e.target.value }))}
            rows={5}
            placeholder={"選択肢1\n選択肢2\n選択肢3"}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            required
          />
        </div>
      )}

      {showScale && (
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="最小値"
            type="number"
            value={formData.scaleMin.toString()}
            onChange={(e) => setFormData((prev) => ({ ...prev, scaleMin: parseInt(e.target.value) }))}
          />
          <Input
            label="最大値"
            type="number"
            value={formData.scaleMax.toString()}
            onChange={(e) => setFormData((prev) => ({ ...prev, scaleMax: parseInt(e.target.value) }))}
          />
          <Input
            label="最小値ラベル"
            value={formData.scaleMinLabel}
            onChange={(e) => setFormData((prev) => ({ ...prev, scaleMinLabel: e.target.value }))}
            placeholder="例：非常に悪い"
          />
          <Input
            label="最大値ラベル"
            value={formData.scaleMaxLabel}
            onChange={(e) => setFormData((prev) => ({ ...prev, scaleMaxLabel: e.target.value }))}
            placeholder="例：非常に良い"
          />
        </div>
      )}

      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isRequired}
            onChange={(e) => setFormData((prev) => ({ ...prev, isRequired: e.target.checked }))}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">必須</span>
        </label>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700 dark:text-gray-300">表示順:</label>
          <Input
            type="number"
            value={formData.order.toString()}
            onChange={(e) => setFormData((prev) => ({ ...prev, order: parseInt(e.target.value) }))}
            className="w-20"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          キャンセル
        </Button>
        <Button type="submit" loading={saving}>
          保存
        </Button>
      </div>
    </form>
  );
}

// アンケート編集フォーム
function SurveyEditForm({
  survey,
  onSave,
  onCancel,
}: {
  survey: Survey;
  onSave: () => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    title: survey.title,
    description: survey.description || '',
    endDate: survey.endDate || '',
    targetCount: survey.targetCount?.toString() || '',
    isAnonymous: survey.isAnonymous,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.put(`/api/surveys/${survey.id}`, {
        ...formData,
        targetCount: formData.targetCount ? parseInt(formData.targetCount) : null,
      });
      onSave();
    } catch {
      toast.error('更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="タイトル"
        value={formData.title}
        onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
        required
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">説明</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="回答期限"
          type="date"
          value={formData.endDate}
          onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
        />
        <Input
          label="目標回答数"
          type="number"
          value={formData.targetCount}
          onChange={(e) => setFormData((prev) => ({ ...prev, targetCount: e.target.value }))}
        />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.isAnonymous}
          onChange={(e) => setFormData((prev) => ({ ...prev, isAnonymous: e.target.checked }))}
          className="w-4 h-4 rounded"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">匿名アンケート</span>
      </label>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          キャンセル
        </Button>
        <Button type="submit" loading={saving}>
          保存
        </Button>
      </div>
    </form>
  );
}

